<template>
  <div class="login-page">

    <!-- 左侧视觉区 -->
    <div class="login-visual">
      <div class="visual-bg" />
      <div class="visual-content">
        <div class="logo-wrap">
          <div class="logo-icon">
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="24" fill="rgba(255,255,255,0.15)" />
              <path d="M14 18c0-2.2 1.8-4 4-4h12c2.2 0 4 1.8 4 4v4c0 6.6-4.5 12-10 12S14 28.6 14 22v-4z" fill="white" fill-opacity="0.9"/>
              <path d="M20 14v-2M28 14v-2M24 14v-3" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
          <span class="logo-name">DR RemoteCheck</span>
        </div>
        <h1 class="visual-title">智能售后远程服务系统</h1>
        <p class="visual-desc">专为设备厂商打造的<br/>远程设备诊断与运维平台</p>
        <div class="visual-features">
          <div class="feature-item">
            <el-icon><Check /></el-icon>
            <span>设备状态实时监控</span>
          </div>
          <div class="feature-item">
            <el-icon><Check /></el-icon>
            <span>异常告警及时推送</span>
          </div>
          <div class="feature-item">
            <el-icon><Check /></el-icon>
            <span>错误日志远程查看</span>
          </div>
        </div>
      </div>
      <div class="visual-footer">© 2026 DR RemoteCheck. All rights reserved.</div>
    </div>

    <!-- 右侧表单区 -->
    <div class="login-right">
      <div class="form-container">

        <!-- 顶部语言/装饰 -->
        <div class="form-header">
          <span class="form-header-tip">
            <template v-if="panel === 'login'"><!-- 没有账号？<span class="link" @click="toPanel('register')">免费注册</span> --></template>
            <template v-else-if="panel === 'register'">已有账号？<span class="link" @click="toPanel('login')">立即登录</span></template>
            <template v-else><span class="link" @click="toPanel('login')">← 返回登录</span></template>
          </span>
        </div>

        <!-- ========== 登录面板 ========== -->
        <transition name="fade" mode="out-in">
          <div v-if="panel === 'login'" key="login" class="panel">
            <div class="panel-title">
              <h2>欢迎回来</h2>
              <p>登录您的账号以继续</p>
            </div>
            <el-form ref="loginFormRef" :model="loginForm" :rules="loginRules" @keyup.enter="handleLogin">
              <el-form-item prop="Email">
                <label class="field-label">邮箱地址</label>
                <el-input
                  v-model="loginForm.Email"
                  placeholder="请输入邮箱"
                  size="large"
                  clearable
                >
                  <template #prefix><el-icon><Message /></el-icon></template>
                </el-input>
              </el-form-item>
              <el-form-item prop="Password">
                <div class="field-label-row">
                  <label class="field-label">密码</label>
                  <span class="link small" @click="toPanel('forgot')">忘记密码？</span>
                </div>
                <el-input
                  v-model="loginForm.Password"
                  type="password"
                  placeholder="请输入密码"
                  size="large"
                  show-password
                  clearable
                >
                  <template #prefix><el-icon><Lock /></el-icon></template>
                </el-input>
              </el-form-item>
              <div class="remember-row">
                <el-checkbox v-model="savePassword">记住密码</el-checkbox>
              </div>
              <el-button class="submit-btn" type="primary" size="large" :loading="loading" @click="handleLogin">
                登录
              </el-button>
            </el-form>
          </div>

          <!-- ========== 注册面板 ========== -->
          <div v-else-if="panel === 'register'" key="register" class="panel">
            <div class="panel-title">
              <h2>创建账号</h2>
              <p>填写以下信息完成注册</p>
            </div>
            <el-form ref="registerFormRef" :model="registerForm" :rules="registerRules">
              <el-form-item prop="Email">
                <label class="field-label">邮箱地址</label>
                <div class="input-code-row">
                  <el-input v-model="registerForm.Email" placeholder="请输入邮箱" size="large" clearable>
                    <template #prefix><el-icon><Message /></el-icon></template>
                  </el-input>
                  <el-button
                    class="code-btn"
                    size="large"
                    :disabled="captchaSent || captchaLoading"
                    :loading="captchaLoading"
                    @click="handleSendCode('register')"
                  >
                    {{ captchaSent ? `${countdown}s` : '获取验证码' }}
                  </el-button>
                </div>
              </el-form-item>
              <el-form-item prop="EmailActivationCode">
                <label class="field-label">邮箱验证码</label>
                <el-input v-model="registerForm.EmailActivationCode" placeholder="请输入收到的验证码" size="large" clearable>
                  <template #prefix><el-icon><Key /></el-icon></template>
                </el-input>
              </el-form-item>
              <el-form-item prop="Password">
                <label class="field-label">设置密码</label>
                <el-input v-model="registerForm.Password" type="password" placeholder="至少 6 位字符" size="large" show-password clearable>
                  <template #prefix><el-icon><Lock /></el-icon></template>
                </el-input>
              </el-form-item>
              <el-button class="submit-btn" type="primary" size="large" :loading="loading" @click="handleRegister">
                注册
              </el-button>
            </el-form>
          </div>

          <!-- ========== 忘记密码面板 ========== -->
          <div v-else-if="panel === 'forgot'" key="forgot" class="panel">
            <div class="panel-title">
              <h2>重置密码</h2>
              <p>通过邮箱验证重置您的密码</p>
            </div>
            <el-form ref="forgotFormRef" :model="forgotForm" :rules="forgotRules">
              <el-form-item prop="Email">
                <label class="field-label">注册邮箱</label>
                <div class="input-code-row">
                  <el-input v-model="forgotForm.Email" placeholder="请输入注册邮箱" size="large" clearable>
                    <template #prefix><el-icon><Message /></el-icon></template>
                  </el-input>
                  <el-button
                    class="code-btn"
                    size="large"
                    :disabled="captchaSent || captchaLoading"
                    :loading="captchaLoading"
                    @click="handleSendCode('forgot')"
                  >
                    {{ captchaSent ? `${countdown}s` : '获取验证码' }}
                  </el-button>
                </div>
              </el-form-item>
              <el-form-item prop="EmailActivationCode">
                <label class="field-label">邮箱验证码</label>
                <el-input v-model="forgotForm.EmailActivationCode" placeholder="请输入收到的验证码" size="large" clearable>
                  <template #prefix><el-icon><Key /></el-icon></template>
                </el-input>
              </el-form-item>
              <el-form-item prop="Password">
                <label class="field-label">新密码</label>
                <el-input v-model="forgotForm.Password" type="password" placeholder="至少 6 位字符" size="large" show-password clearable>
                  <template #prefix><el-icon><Lock /></el-icon></template>
                </el-input>
              </el-form-item>
              <el-button class="submit-btn" type="primary" size="large" :loading="loading" @click="handleResetPassword">
                重置密码
              </el-button>
            </el-form>
          </div>
        </transition>

      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { Message, Lock, Key, Check } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import { postRequest } from '@/tools/axios/http'
import { PublicStore, loginStore } from '@/tools/pinia/store'

const router = useRouter()
const publicData = PublicStore()
const loginData = loginStore()

const panel = ref('login')
const loading = ref(false)
const captchaSent = ref(false)
const captchaLoading = ref(false)
const countdown = ref(60)
let countdownTimer = null

// ===================== 登录 =====================
const loginFormRef = ref(null)
const savePassword = ref(false)
const loginForm = reactive({ Email: '', Password: '' })
const loginRules = {
  Email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '邮箱格式不正确', trigger: 'blur' },
  ],
  Password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
}

onMounted(() => {
  if (loginData.savePasswordChecked) {
    loginForm.Email = loginData.account
    loginForm.Password = loginData.password
    savePassword.value = true
  }
})

async function handleLogin() {
  await loginFormRef.value.validate(async (valid) => {
    if (!valid) return
    loading.value = true
    try {
      const res = await postRequest('/dental/base/login', {
        Email: loginForm.Email,
        Password: loginForm.Password,
      })
      if (res.code === 200) {
        publicData.changeAccessToken(res.data.AccessToken)
        publicData.changeRefreshToken(res.data.RefreshToken)
        publicData.changeUserInfo(res.data.UserInfo)
        loginData.changeSavePasswordChecked(savePassword.value)
        if (savePassword.value) {
          loginData.changeAccount(loginForm.Email)
          loginData.changePassword(loginForm.Password)
        } else {
          loginData.changeAccount('')
          loginData.changePassword('')
        }
        ElMessage.success('登录成功')
        router.push('/home')
      } else {
        ElMessage.error(res.msg || '账号或密码错误')
      }
    } catch {
      ElMessage.error('网络异常，请稍后重试')
    } finally {
      loading.value = false
    }
  })
}

// ===================== 注册 =====================
const registerFormRef = ref(null)
const registerForm = reactive({ Email: '', EmailActivationCode: '', Password: '' })
const registerRules = {
  Email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '邮箱格式不正确', trigger: 'blur' },
  ],
  EmailActivationCode: [{ required: true, message: '请输入验证码', trigger: 'blur' }],
  Password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, message: '密码至少 6 位', trigger: 'blur' },
  ],
}

async function handleRegister() {
  await registerFormRef.value.validate(async (valid) => {
    if (!valid) return
    loading.value = true
    try {
      const verifyRes = await postRequest('/dental/base/validateActivationEmail', {
        Email: registerForm.Email,
        EmailActivationCode: registerForm.EmailActivationCode,
      })
      if (verifyRes.code !== 200) {
        ElMessage.error(verifyRes.msg || '验证码错误或已过期')
        return
      }
      const res = await postRequest('/dental/base/register', {
        Email: registerForm.Email,
        EmailActivationCode: registerForm.EmailActivationCode,
        Password: registerForm.Password,
        UserType: '1',
      })
      if (res.code === 200) {
        ElMessage.success('注册成功，请登录')
        toPanel('login')
      } else {
        ElMessage.error(res.msg || '注册失败')
      }
    } catch {
      ElMessage.error('网络异常，请稍后重试')
    } finally {
      loading.value = false
    }
  })
}

// ===================== 忘记密码 =====================
const forgotFormRef = ref(null)
const forgotForm = reactive({ Email: '', EmailActivationCode: '', Password: '' })
const forgotRules = {
  Email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '邮箱格式不正确', trigger: 'blur' },
  ],
  EmailActivationCode: [{ required: true, message: '请输入验证码', trigger: 'blur' }],
  Password: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, message: '密码至少 6 位', trigger: 'blur' },
  ],
}

async function handleResetPassword() {
  await forgotFormRef.value.validate(async (valid) => {
    if (!valid) return
    loading.value = true
    try {
      const res = await postRequest('/dental/base/resetPassword', {
        Email: forgotForm.Email,
        EmailActivationCode: forgotForm.EmailActivationCode,
        Password: forgotForm.Password,
      })
      if (res.code === 200) {
        ElMessage.success('密码重置成功，请重新登录')
        toPanel('login')
      } else {
        ElMessage.error(res.msg || '重置失败，验证码错误或已过期')
      }
    } catch {
      ElMessage.error('网络异常，请稍后重试')
    } finally {
      loading.value = false
    }
  })
}

// ===================== 发送验证码 =====================
async function handleSendCode(type) {
  const email = type === 'register' ? registerForm.Email : forgotForm.Email
  const formRef = type === 'register' ? registerFormRef : forgotFormRef

  formRef.value.validateField('Email', async (errMsg) => {
    if (errMsg) return
    captchaLoading.value = true
    try {
      const body = { Email: email }
      if (type === 'forgot') body.Type = 'forgotPassword'
      const res = await postRequest('/dental/base/sendActivationEmail', body)
      if (res.code === 200) {
        ElMessage.success('验证码已发送，请查收邮件')
        startCountdown()
      } else {
        ElMessage.error(res.msg || '发送失败')
      }
    } catch {
      ElMessage.error('网络异常，请稍后重试')
    } finally {
      captchaLoading.value = false
    }
  })
}

function startCountdown() {
  captchaSent.value = true
  countdown.value = 60
  countdownTimer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownTimer)
      captchaSent.value = false
    }
  }, 1000)
}

function toPanel(target) {
  panel.value = target
  captchaSent.value = false
  countdown.value = 60
  clearInterval(countdownTimer)
}
</script>

<style scoped lang="scss">
// ─── 整体布局 ───────────────────────────────────────────
.login-page {
  display: flex;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #fff;
}

// ─── 左侧视觉区 ─────────────────────────────────────────
.login-visual {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 60px 48px;

  .visual-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(145deg, #1557e0 0%, #0b3bb5 50%, #072d8a 100%);
    z-index: 0;

    &::before {
      content: '';
      position: absolute;
      width: 500px;
      height: 500px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.04);
      top: -120px;
      right: -80px;
    }

    &::after {
      content: '';
      position: absolute;
      width: 360px;
      height: 360px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.05);
      bottom: -80px;
      left: -60px;
    }
  }

  .visual-content {
    position: relative;
    z-index: 1;
    color: #fff;
    max-width: 420px;
  }

  .logo-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 48px;

    .logo-icon {
      width: 44px;
      height: 44px;
      svg { width: 100%; height: 100%; }
    }

    .logo-name {
      font-size: 22px;
      font-weight: 700;
      letter-spacing: 1px;
      color: #fff;
    }
  }

  .visual-title {
    font-size: 38px;
    font-weight: 700;
    line-height: 1.2;
    margin: 0 0 20px;
    color: #fff;
  }

  .visual-desc {
    font-size: 16px;
    line-height: 1.8;
    color: rgba(255, 255, 255, 0.7);
    margin: 0 0 48px;
  }

  .visual-features {
    display: flex;
    flex-direction: column;
    gap: 14px;

    .feature-item {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 15px;
      color: rgba(255, 255, 255, 0.85);

      .el-icon {
        width: 20px;
        height: 20px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        flex-shrink: 0;
      }
    }
  }

  .visual-footer {
    position: absolute;
    bottom: 28px;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.35);
    z-index: 1;
  }
}

// ─── 右侧表单区 ─────────────────────────────────────────
.login-right {
  width: 500px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fafbfc;
  border-left: 1px solid #eef0f3;
}

.form-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 60px;
  justify-content: center;
}

// 顶部切换提示
.form-header {
  position: absolute;
  top: 32px;
  right: 48px;
  font-size: 14px;
  color: #8a92a6;
}

.login-right {
  position: relative;
}

// 面板标题
.panel-title {
  margin-bottom: 36px;

  h2 {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px;
  }

  p {
    font-size: 14px;
    color: #9ca3af;
    margin: 0;
  }
}

// 字段标签
.field-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;

  .field-label {
    margin-bottom: 0;
  }
}

// 覆盖 el-form-item 底部间距
:deep(.el-form-item) {
  margin-bottom: 20px;

  .el-form-item__content {
    display: block;
  }

  .el-form-item__error {
    font-size: 12px;
  }
}

// 输入框 + 验证码按钮横排
.input-code-row {
  display: flex;
  gap: 10px;

  .el-input { flex: 1; }

  .code-btn {
    flex-shrink: 0;
    width: 108px;
    font-size: 13px;
    border-radius: 8px;
  }
}

// 记住密码
.remember-row {
  margin-bottom: 24px;
  margin-top: -4px;
}

// 提交按钮
.submit-btn {
  width: 100%;
  height: 46px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 10px;
  letter-spacing: 1px;
  margin-top: 4px;
}

// 链接文字
.link {
  color: #1557e0;
  cursor: pointer;
  font-weight: 500;

  &:hover { text-decoration: underline; }
  &.small { font-size: 13px; }
}

// 面板切换动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.fade-enter-from {
  opacity: 0;
  transform: translateX(10px);
}
.fade-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}
</style>
